import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Modal,
  TextField,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import {
  getDocumentTypes,
  uploadDocuments,
} from "../service/CompanyDocumentsService";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

export const suggestDocumentType = (filename, types) => {
  const normalized = filename.toUpperCase().replace(/[^A-Z0-9]+/g, "_");
  const matches = types.filter((type) =>
    `_${normalized}_`.includes(`_${type.code}_`),
  );
  return matches.length === 1 ? matches[0].id : "";
};

export const validateUploadRow = (
  row,
  types,
  rows,
  t,
) => {
  const message = (
    key,
    fallback,
    params,
  ) =>
    typeof t === "function"
      ? t(key, params)
      : fallback;

  const extension = row.file.name.split(".").pop().toLowerCase();
  if (extension !== "pdf" || (row.file.type && row.file.type !== "application/pdf"))
    return message(
      "client.upload.validation.pdfOnly",
      "Hanya file PDF yang diperbolehkan.",
    );
  const type = types.find((item) => item.id === Number(row.typeId));
  if (!type)
    return message(
      "client.upload.validation.selectType",
      "Select a document type",
    );
  if (rows.filter((item) => Number(item.typeId) === type.id).length > 1)
    return message(
      "client.upload.validation.uniqueType",
      "Each file needs a different document type",
    );
  if (!type.allowed_extensions.includes(extension))
    return message(
      "client.upload.validation.unsupported",
      "Unsupported extension",
    );
  if (row.file.size === 0)
    return message(
      "client.upload.validation.empty",
      "File is empty",
    );
  if (row.file.size > type.max_file_size)
    return message(
      "client.upload.validation.maximum",
      `Maximum ${type.max_file_size / 1048576} MB`,
      {
        max:
          type.max_file_size /
          1048576,
      },
    );
  return "";
};

export default function DocumentUploadModal({
  documentType,
  allowedTypeIds,
  scope,
  shareholderName,
  onClose,
  onSaved,
}) {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { setMessage } = useSnackbar();
  const { t } =
    useCompanyDocumentsLanguage();

  const bulk = !documentType;

  useEffect(() => {
    let active = true;
    getDocumentTypes()
      .then((data) => {
        if (active)
          setTypes(
            allowedTypeIds
              ? data.filter((type) => allowedTypeIds.includes(type.id))
              : data.filter((type) => type.section !== "B"),
          );
      })
      .catch((error) => {
        if (active) setError(error.message);
      })
      .finally(() => {
        if (active) setLoadingTypes(false);
      });
    return () => {
      active = false;
    };
  }, [allowedTypeIds]);

  const errors = useMemo(
    () =>
      rows.map((row) =>
        validateUploadRow(
          row,
          types,
          rows,
          t,
        ),
      ),
    [rows, types, t],
  );
  const totalSize = rows.reduce((total, row) => total + row.file.size, 0);
  const canSave =
    !loadingTypes &&
    !busy &&
    rows.length > 0 &&
    errors.every((error) => !error) &&
    totalSize < 49 * 1048576 &&
    (!bulk || confirmed);

  const selectFiles = (files) => {
    if (busy || loadingTypes) return;
    const selected = Array.from(files || []);
    if (!bulk && selected.length !== 1) {
      setError(
        t("client.upload.chooseOne"),
      );
      return;
    }
    const next = selected.map((file) => ({
      file,
      typeId: documentType?.id || suggestDocumentType(file.name, types),
    }));
    if ((bulk ? rows.length : 0) + next.length > 10) {
      setError(
        t("client.upload.maxTen"),
      );
      return;
    }
    setRows(bulk ? [...rows, ...next] : next);
    setConfirmed(false);
    setError("");
  };

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError("");
    try {
      const results = scope
        ? await uploadDocuments(rows, scope)
        : await uploadDocuments(rows);
      const duplicates = results.filter(
        (item) => item.identical_to_previous,
      ).length;
      setMessage({
        type: "success",
        desc:
          t(
            "client.upload.success",
            {
              count: results.length,
              suffix:
                results.length === 1
                  ? ""
                  : "s",
            },
          ) +
          (duplicates
            ? t(
                "client.upload.duplicate",
                {
                  count: duplicates,
                },
              )
            : ""),
      });
      onSaved();
    } catch (error) {
      setError(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={busy ? undefined : onClose}
      aria-labelledby="document-upload-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "94%", md: 850 },
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "90vh",
          boxSizing: "border-box",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 3,
        }}
      >
        <h2 id="document-upload-title" className="text-xl font-bold mb-2">
          {bulk
            ? t(
                "client.upload.bulkTitle",
              )
            : t(
                "client.upload.title",
                {
                  name:
                    documentType.name,
                },
              )}
        </h2>
        {shareholderName && (
          <Alert severity="info" className="mb-3">
            {t(
              "client.upload.shareholder",
              {
                name:
                  shareholderName,
              },
            )}
          </Alert>
        )}
        <p className="text-sm text-[#5F737F] mb-5">
          {bulk
            ? t(
                "client.upload.bulkDescription",
              )
            : t(
                "client.upload.singleDescription",
              )}
        </p>
        <p className="text-sm mb-3">
          {t("client.upload.pdfOnly")}
        </p>
        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}
        {loadingTypes ? (
          <CircularProgress size={24} />
        ) : (
          <div
            className={`border border-dashed rounded-lg p-8 text-center bg-[#556FB50D] ${hovered ? "border-[#556FB5]" : "border-[#CDD4D7]"}`}
            onDragOver={(event) => {
              event.preventDefault();
              setHovered(true);
            }}
            onDragLeave={() => setHovered(false)}
            onDrop={(event) => {
              event.preventDefault();
              setHovered(false);
              selectFiles(event.dataTransfer.files);
            }}
          >
            <CloudUploadOutlinedIcon
              className="text-[#556FB5] mb-2"
              fontSize="large"
            />
            <p className="text-sm mb-2">
              {bulk
                ? t(
                    "client.upload.dragFiles",
                  )
                : t(
                    "client.upload.dragFile",
                  )}
            </p>
            <Button
              component="label"
              variant="outlined"
              disabled={busy || !types.length}
            >
              {t(
                "client.upload.browse",
              )}
              <input
                aria-label={t(
                  "client.upload.inputAria",
                )}
                type="file"
                hidden
                multiple={bulk}
                accept="application/pdf,.pdf"
                onChange={(event) => {
                  selectFiles(event.target.files);
                  event.target.value = "";
                }}
              />
            </Button>
            <p className="text-xs text-[#5F737F] mt-3">
              {documentType
                ? t(
                    "client.upload.maxSingle",
                    {
                      max:
                        documentType.max_file_size /
                        1048576,
                    },
                  )
                : t(
                    "client.upload.maxBulk",
                  )}
            </p>
          </div>
        )}
        <div className="grid gap-3 mt-5">
          {rows.map((row, index) => (
            <div
              key={index}
              className="grid grid-cols-1 md:grid-cols-[1fr_240px_150px_70px] gap-3 items-center border-b pb-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold break-all">
                  {row.file.name}
                </p>
                <p className="text-xs text-[#5F737F]">
                  {(row.file.size / 1048576).toFixed(2)} MB
                </p>
              </div>
              {bulk ? (
                <TextField
                  select
                  size="small"
                  label={t(
                    "client.upload.typeFor",
                    {
                      name:
                        row.file.name,
                    },
                  )}
                  value={row.typeId}
                  disabled={busy}
                  onChange={(event) => {
                    setRows(
                      rows.map((item, i) =>
                        i === index
                          ? { ...item, typeId: event.target.value }
                          : item,
                      ),
                    );
                    setConfirmed(false);
                  }}
                >
                  <MenuItem value="">
                    {t(
                      "client.upload.selectType",
                    )}
                  </MenuItem>
                  {types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <p className="text-sm">{documentType.name}</p>
              )}
              <p
                className={`text-xs ${errors[index] ? "text-red-700" : "text-green-700"}`}
              >
                {errors[index] ||
                  t(
                    "client.upload.ready",
                  )}
              </p>
              <Button
                size="small"
                disabled={busy}
                color="error"
                onClick={() => {
                  setRows(rows.filter((_, i) => i !== index));
                  setConfirmed(false);
                }}
              >
                {t(
                  "client.upload.remove",
                )}
              </Button>
            </div>
          ))}
        </div>
        {totalSize >= 49 * 1048576 && (
          <Alert severity="error" className="mt-4">
            {t(
              "client.upload.combined",
            )}
          </Alert>
        )}
        {rows.length > 0 && (
          <>
            {bulk && (
              <FormControlLabel
                className="mt-3"
                control={
                  <Checkbox
                    checked={confirmed}
                    disabled={busy}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                }
                label={t(
                  "client.upload.confirm",
                )}
              />
            )}
          </>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <Button disabled={busy} onClick={onClose}>
            {t(
              "client.upload.cancel",
            )}
          </Button>
          <Button
            variant="contained"
            disabled={!canSave}
            onClick={save}
            startIcon={
              busy ? <CircularProgress color="inherit" size={18} /> : null
            }
          >
            {busy
              ? t(
                  "client.upload.uploading",
                )
              : t(
                  "client.upload.submit",
                )}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}
